/**
 * StorageAccessService.js
 * 
 * Service to access storage URLs with signed URLs and caching.
 */

// Firebase
import { functions, httpsCallable } from 'firebaseConfig';

// Constants
const SIGNED_URL_FN = 'getSignedStorageUrl';
const HLS_MANIFEST_FN = 'getSecureHlsManifestUrl';

// Cache for signed URLs
const signedUrlCache = new Map();

// Tracks active signed URL fetches to dedupe concurrent requests.
const signedUrlInFlight = new Map();

// Cache for manifest URLs
const manifestUrlCache = new Map();

// Tracks active manifest URL fetches to dedupe concurrent requests.
const manifestUrlInFlight = new Map();

// Cache for recent failures to avoid spamming the network/console.
const signedUrlFailureCache = new Map();

/**
 * Gets the current time in milliseconds.
 * 
 * @returns {number} Current time in milliseconds.
 */
function nowMs() {
  return Date.now();
}

/**
 * Gets a cached value if it exists and is not expired.
 * 
 * @param {Map} map - The cache map.
 * @param {string} key - The cache key.
 * 
 * @returns {any|null} - The cached value or null if not found/expired.
 */
function getCached(map, key) {

  // Check for existing entry
  const hit = map.get(key);

  // Not found
  if (!hit) return null;

  // Expired
  if (hit.expiresAtMs && hit.expiresAtMs < nowMs()) {
    map.delete(key);
    return null;
  }

  // Valid cache hit
  return hit.value;
}

function isProbablySignedOrPublicUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const u = String(url);

  // Local object URLs
  if (u.startsWith('blob:') || u.startsWith('data:')) return true;

  // Already a full URL (not a raw storagePath)
  if (!u.includes('://')) return false;

  // Firebase Storage download URLs usually include a token and are directly usable.
  // Example: .../o/<path>?alt=media&token=...
  if (/\btoken=/.test(u)) return true;

  // GCS V4 signed URL params
  if (/\bX-Goog-(Algorithm|Credential|Date|Expires|Signature)=/i.test(u)) return true;

  // Older GCS signed URL params
  if (/\b(GoogleAccessId|Signature|Expires)=/i.test(u)) return true;

  return false;
}

// Sets a value in the cache with optional TTL.
function setCached(map, key, value, ttlMs) {
  map.set(key, {
    value,
    expiresAtMs: ttlMs ? (nowMs() + ttlMs) : null,
  });
}

/**
 * Infers the storage object path from a given URL.
 * 
 * Supports raw storage paths, GCS URLs, and Firebase Storage URLs.
 * 
 * @param {string} url - The input URL.
 * 
 * @returns {string|null} - The inferred storage object path or null if not found.
 */
export function inferStoragePathFromUrl(url) {

  // Basic validation
  if (!url || typeof url !== 'string') return null;

  // Trim whitespace
  const trimmed = String(url).trim();

  // Strip query and fragment for raw paths
  const withoutQuery = trimmed.split('?')[0].split('#')[0];

  // Raw storage object paths (preferred now that Storage reads are locked down)
  // e.g. media/<secureFolder>/<nodeKey>/original.jpg or uploads/<...>
  if (
    withoutQuery &&
    !withoutQuery.includes('://') &&
    !withoutQuery.startsWith('gs://') &&
    !withoutQuery.startsWith('blob:') &&
    !withoutQuery.startsWith('data:')
  ) {
    const normalized = withoutQuery.replace(/^\/+/, '');
    if (
      normalized.startsWith('media/') ||
      normalized.startsWith('uploads/') ||
      normalized.startsWith('products/') ||
      normalized.startsWith('nodes/') ||
      normalized.startsWith('workspaces/')
    ) {
      return normalized;
    }
  }

  // https://storage.googleapis.com/<bucket>/<path>
  const gcs = url.match(/^https:\/\/storage\.googleapis\.com\/([^/]+)\/(.+)$/i);
  if (gcs && gcs[2]) {
    // Signed GCS URLs include query params; they are not part of the object name.
    const objectPath = String(gcs[2]).split('?')[0].split('#')[0];
    return decodeURIComponent(objectPath);
  }

  // https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<encodedPath>?...
  const fb = url.match(/^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[^/]+\/o\/([^?]+)(\?.*)?$/i);
  if (fb && fb[1]) {
    return decodeURIComponent(fb[1]);
  }

  return null;
}

/**
 * Gets a signed URL for accessing a storage object.
 * 
 * @param {Object} params - Parameters object.
 * @param {string} [params.nodeKey] - The node key (optional).
 * @param {string} params.storagePath - The storage object path.
 * @param {number} [params.ttlSeconds] - Time-to-live in seconds for the signed URL (optional).
 * 
 * @returns {Promise<string|null>} - The signed URL or null if not available.
 */
export async function getSignedUrlForStoragePath({ nodeKey, workspaceKey, storagePath, ttlSeconds } = {}) {

  // Validate input
  if (!storagePath) return null;

  // Cache key
  const ttlKey = typeof ttlSeconds === 'number' ? ttlSeconds : 'default';

  // Check cache
  const cacheKey = `${nodeKey || 'none'}::${workspaceKey || 'none'}::${storagePath}::${ttlKey}`;

  // Check cache
  const cached = getCached(signedUrlCache, cacheKey);

  // Return cached if available
  if (cached) return cached;

  // If we recently failed to sign this exact thing, don't hammer the endpoint.
  const recentFailure = getCached(signedUrlFailureCache, cacheKey);
  if (recentFailure) return null;

  // If another caller is already resolving this key, reuse that promise.
  const inFlight = signedUrlInFlight.get(cacheKey);
  if (inFlight) return inFlight;

  const task = (async () => {
    // Call the Cloud Function to get a signed URL
    const call = httpsCallable(functions, SIGNED_URL_FN);

    try {
      // Make the call
      const result = await call({
        nodeKey: nodeKey || null,
        workspaceKey: workspaceKey || null,
        storagePath,
        ttlSeconds: ttlSeconds || null,
      });

      // Extract URL
      const url = result?.data?.url || null;

      // Default signed URLs are 4 hours; cache slightly less to avoid edge expiry.
      setCached(signedUrlCache, cacheKey, url, 3.5 * 60 * 60 * 1000);

      return url;
    } catch (e) {
      // Avoid repeated spam for the same key; try again later.
      setCached(signedUrlFailureCache, cacheKey, true, 30 * 1000);
      return null;
    } finally {
      signedUrlInFlight.delete(cacheKey);
    }
  })();

  signedUrlInFlight.set(cacheKey, task);
  return task;
}

/**
 * Resolves an image URL, returning a signed URL if the image is stored in Storage.
 * 
 * @param {Object} params - Parameters object.
 * @param {string} [params.nodeKey] - The node key (optional).
 * @param {string} [params.url] - The original image URL (optional).
 * @param {string} [params.storagePath] - The storage object path (optional).
 * @param {number} [params.ttlSeconds] - Time-to-live in seconds for the signed URL (optional).
 * 
 * @returns {Promise<string|null>} - The resolved image URL.
 */
export async function resolveImageUrl({ nodeKey, workspaceKey, url, storagePath, ttlSeconds } = {}) {

  // Respect an explicit storagePath so callers can force a fresh signed URL
  // even if url is currently a tokenized/signed URL that may be stale.
  const explicitPath = typeof storagePath === 'string' ? String(storagePath).trim() : '';

  // If it already looks like a directly usable signed/public URL, don't re-sign it.
  // This prevents unnecessary callable requests (and noise) for Firebase download-token URLs.
  if (!explicitPath && isProbablySignedOrPublicUrl(url)) return url;

  // Infer storage path if not provided
  const inferred = inferStoragePathFromUrl(url);

  // Determine effective storage path
  const effectivePath = explicitPath || inferred;

  // For node-scoped paths (e.g. nodes/<nodeKey>/cover/...), infer nodeKey from the path
  // if not explicitly provided, so the media gate can authorize access.
  let effectiveNodeKey = nodeKey || null;
  let effectiveWorkspaceKey = workspaceKey || null;
  if (!effectiveNodeKey && typeof effectivePath === 'string') {
    const m = effectivePath.match(/^nodes\/([^/]+)\//);
    if (m && m[1]) effectiveNodeKey = m[1];

    // Media objects are generally stored under media/<secureFolder>/<nodeKey>/...
    // Infer nodeKey when possible to reduce failed sign attempts.
    const mediaMatch = effectivePath.match(/^media\/[^/]+\/([^/]+)\//);
    if (!effectiveNodeKey && mediaMatch && mediaMatch[1]) {
      effectiveNodeKey = mediaMatch[1];
    }
  }

  if (typeof effectivePath === 'string') {
    const workspaceMatch = effectivePath.match(/^workspaces\/([^/]+)\//);
    if (workspaceMatch && workspaceMatch[1]) {
      effectiveWorkspaceKey = workspaceMatch[1];
    }
  }

  // If we have a storage path, get a signed URL
  if (effectivePath) {
    return getSignedUrlForStoragePath({
      nodeKey: effectiveNodeKey,
      workspaceKey: effectiveWorkspaceKey,
      storagePath: effectivePath,
      ttlSeconds,
    });
  }

  // Otherwise, return the original URL
  return url || null;
}

/**
 * Resolves a file URL, returning a signed URL if the file is stored in Storage.
 * 
 * @param {Object} params - Parameters object.
 * @param {string} [params.nodeKey] - The node key (optional).
 * @param {string} [params.url] - The original file URL (optional).
 * @param {string} [params.storagePath] - The storage object path (optional).
 * @param {number} [params.ttlSeconds] - Time-to-live in seconds for the signed URL (optional).
 * 
 * @returns {Promise<string|null>} - The resolved file URL.
 */
export async function resolveFileUrl({ nodeKey, url, storagePath, ttlSeconds } = {}) {
  return resolveImageUrl({ nodeKey, url, storagePath, ttlSeconds });
}

/**
 * Gets a secure HLS manifest URL for streaming.
 * 
 * @param {Object} params - Parameters object.
 * @param {string} params.nodeKey - The node key.
 * @param {string} params.manifestPath - The storage path to the HLS manifest.
 * 
 * @returns {Promise<string|null>} - The secure HLS manifest URL.
 */
export async function getSecureHlsManifestUrl({ nodeKey, manifestPath } = {}) {

  // Validate input
  if (!nodeKey || !manifestPath) return null;

  // Cache key
  const cacheKey = `${nodeKey}::${manifestPath}`;

  // Check cache
  const cached = getCached(manifestUrlCache, cacheKey);

  // Return cached if available
  if (cached) return cached;

  // Reuse active request for the same manifest.
  const inFlight = manifestUrlInFlight.get(cacheKey);
  if (inFlight) return inFlight;

  const task = (async () => {
    // Call the Cloud Function to get the secure HLS manifest URL
    const call = httpsCallable(functions, HLS_MANIFEST_FN);

    // Make the call
    const result = await call({ nodeKey, manifestPath });

    // Extract URL
    const url = result?.data?.url || null;

    // Ticket TTL is 10 minutes; cache slightly less.
    setCached(manifestUrlCache, cacheKey, url, 9 * 60 * 1000);

    return url;
  })();

  manifestUrlInFlight.set(cacheKey, task);

  try {
    // Return URL
    return await task;
  } finally {
    manifestUrlInFlight.delete(cacheKey);
  }
}

/**
 * Resolves a playable video URL, returning a secure HLS manifest URL or signed URL as needed.
 * 
 * @param {Object} node - The video node object.
 * 
 * @returns {Promise<string|null>} - The playable video URL.
 */
export async function resolvePlayableVideoUrl(node) {

  // Validate input
  if (!node) return null;

  // Extract relevant fields
  const nodeKey = node.key || null;

  // Determine raw URL and storage path
  const rawUrl = node.url || node.data_video?.url || null;

  // Determine storage path
  const storagePath = node.storagePath || node.data_video?.storagePath || null;

  // If already a secure manifest URL, return as is
  if (typeof rawUrl === 'string' && rawUrl.includes('/secureHlsManifest?ticket=')) {
    return rawUrl;
  }

  // Infer storage path if not provided
  const inferred = inferStoragePathFromUrl(rawUrl);

  // Determine effective storage path
  const effectivePath = storagePath || inferred;

  // Check if HLS
  const isHls = (typeof rawUrl === 'string' && rawUrl.endsWith('.m3u8'))
    || (typeof effectivePath === 'string' && effectivePath.endsWith('.m3u8'));

  // Return appropriate URL
  if (isHls && nodeKey && effectivePath) {
    return getSecureHlsManifestUrl({ nodeKey, manifestPath: effectivePath });
  }

  // Otherwise, return signed URL if storage path exists
  if (effectivePath) {
    return getSignedUrlForStoragePath({ nodeKey, storagePath: effectivePath });
  }

  // Otherwise, return raw URL
  return rawUrl;
}
